import React from 'react';
import {
  Apple,
  ArrowRight,
  BarChart3,
  Bell,
  Building2,
  Check,
  ChevronRight,
  CreditCard,
  FileText,
  Home,
  LayoutDashboard,
  Menu,
  MessageSquare,
  Package,
  Play,
  Shield,
  Users,
  Wrench,
  X,
  Zap
} from 'lucide-react';
import Footer from './Footer';

const HomePage = ({ showPage }) => {
  return (
    <div className="page active">

      <section className="relative min-h-[90vh] flex items-center overflow-hidden" style={{ background: 'linear-gradient(135deg, #1A2A6C 0%, #2D52B8 50%, #4F8EF7 100%)' }}>
        {/* Dot pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1A2A6C]/30 to-transparent"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-32 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-xs font-semibold mb-8 backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                Trusted by 1,200+ properties across Nigeria
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
                Property management,{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-white">reimagined.</span>
              </h1>
              <p className="text-lg text-blue-100 mb-10 leading-relaxed max-w-lg">
                The all-in-one platform that helps property managers collect rent, sell electricity, and delight residents — while residents enjoy a seamless living experience.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button
                  onClick={() => window.location.href = 'https://admin.bilm.app/auth/sign-up'}
                  className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold rounded-small text-primary-900 bg-white hover:bg-blue-50 shadow-lg transition-all gap-2"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </button>
                <a
                  href="https://apps.apple.com/ng/app/bilm/id6471915924"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-7 py-3.5 border border-white/30 text-base font-semibold rounded-small text-white hover:bg-white/10 transition-all gap-2 backdrop-blur-sm"
                >
                  <Apple className="w-5 h-5" /> Download App
                </a>
              </div>
              {/* Trust badges */}
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-blue-200">
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-400" /> 5 min setup</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-400" /> No long-term contracts</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-400" /> Free migration support</span>
              </div>
            </div>

            {/* Right mockup: dashboard card behind + phone in front */}
            <div className="relative flex items-center justify-center lg:justify-end">
              {/* Dashboard card (behind) */}
              <div className="hidden lg:block absolute -top-4 -left-4 w-[340px] bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 shadow-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <LayoutDashboard className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-sm font-semibold">Admin Dashboard</div>
                    <div className="text-blue-200 text-xs">The Lofts Estate</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/10 rounded-lg p-3">
                    <div className="text-blue-200 text-xs">Collection Rate</div>
                    <div className="text-white font-bold text-lg">94.2%</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3">
                    <div className="text-blue-200 text-xs">Monthly Revenue</div>
                    <div className="text-white font-bold text-lg">N8.4M</div>
                  </div>
                </div>
              </div>

              {/* Phone mockup (in front) */}
              <div className="relative z-10 animate-float">
                <div className="w-[270px] h-[560px] bg-neutral-900 rounded-[2.5rem] p-3 shadow-2xl border-4 border-neutral-800">
                  <div className="w-full h-full bg-white rounded-[2rem] overflow-hidden flex flex-col">
                    <div className="bg-primary-600 p-6 pb-10 text-white">
                      <div className="flex justify-between items-center mb-5">
                        <Menu className="w-5 h-5" />
                        <Bell className="w-5 h-5" />
                      </div>
                      <h3 className="font-heading text-xl font-bold">Hello, Alex</h3>
                      <p className="text-primary-100 text-sm">Unit 402 - The Lofts</p>
                    </div>
                    <div className="flex-1 bg-neutral-50 -mt-5 rounded-t-3xl p-4 space-y-3 overflow-hidden">
                      <div className="bg-white p-4 rounded-large shadow-sm border border-neutral-100">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Rent Due</span>
                          <span className="text-xs text-primary-600 font-semibold">3 days</span>
                        </div>
                        <div className="text-2xl font-bold text-neutral-900 font-heading">N85,000</div>
                        <div className="w-full mt-3 py-2 bg-neutral-900 text-white text-xs font-semibold rounded-small text-center">Pay Now</div>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 p-3 rounded-large flex items-center gap-3">
                        <div className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center shrink-0">
                          <Zap className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-amber-800">Electricity</div>
                          <div className="text-sm font-bold text-amber-900">42.5 kWh</div>
                        </div>
                        <span className="text-xs bg-amber-500 text-white px-2 py-1 rounded font-semibold shrink-0">Buy</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-white p-2 rounded-large shadow-sm border border-neutral-100 flex flex-col items-center gap-1">
                          <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center">
                            <Wrench className="w-3 h-3" />
                          </div>
                          <span className="text-[10px] font-medium text-neutral-700 text-center leading-tight">Repairs</span>
                        </div>
                        <div className="bg-white p-2 rounded-large shadow-sm border border-neutral-100 flex flex-col items-center gap-1">
                          <div className="w-8 h-8 bg-green-50 text-green-600 rounded-full flex items-center justify-center">
                            <Package className="w-3 h-3" />
                          </div>
                          <span className="text-[10px] font-medium text-neutral-700 text-center leading-tight">Packages</span>
                        </div>
                        <div className="bg-white p-2 rounded-large shadow-sm border border-neutral-100 flex flex-col items-center gap-1">
                          <div className="w-8 h-8 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center">
                            <Users className="w-3 h-3" />
                          </div>
                          <span className="text-[10px] font-medium text-neutral-700 text-center leading-tight">Community</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 bg-white border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="font-heading text-2xl sm:text-3xl font-bold text-neutral-900">1,200+</div>
              <div className="text-sm text-neutral-500">Properties</div>
            </div>
            <div>
              <div className="font-heading text-2xl sm:text-3xl font-bold text-neutral-900">50,000+</div>
              <div className="text-sm text-neutral-500">Residents</div>
            </div>
            <div>
              <div className="font-heading text-2xl sm:text-3xl font-bold text-neutral-900">N2B+</div>
              <div className="text-sm text-neutral-500">Processed</div>
            </div>
            <div>
              <div className="font-heading text-2xl sm:text-3xl font-bold text-neutral-900">99.9%</div>
              <div className="text-sm text-neutral-500">Uptime</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">One platform, two powerful experiences</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">Whether you manage properties or live in one, Bilm has the tools you need.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* For Property Managers - dark card */}
            <div className="bg-neutral-900 rounded-2xl p-8 text-white relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-primary-600/20 to-transparent rounded-bl-full"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mb-6">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-3">For Property Managers</h3>
                <p className="text-neutral-400 text-sm mb-6">Automate operations, collect rent faster, and keep residents happy.</p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-sm text-neutral-300">
                    <Check className="w-4 h-4 text-primary-400 shrink-0" /> Automated rent collection & reminders
                  </li>
                  <li className="flex items-center gap-3 text-sm text-neutral-300">
                    <Check className="w-4 h-4 text-primary-400 shrink-0" /> Electricity vending with markup control
                  </li>
                  <li className="flex items-center gap-3 text-sm text-neutral-300">
                    <Check className="w-4 h-4 text-primary-400 shrink-0" /> Maintenance request tracking
                  </li>
                  <li className="flex items-center gap-3 text-sm text-neutral-300">
                    <Check className="w-4 h-4 text-primary-400 shrink-0" /> Financial reports & analytics
                  </li>
                </ul>
                <button
                  onClick={() => showPage('/managers')}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-primary-400 transition-colors"
                >
                  Learn more <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* For Residents - light card */}
            <div className="bg-white rounded-2xl p-8 border-2 border-primary-100 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-primary-50 to-transparent rounded-bl-full"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-6">
                  <Home className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-neutral-900 mb-3">For Residents</h3>
                <p className="text-neutral-500 text-sm mb-6">Pay rent, buy electricity, and manage your home from your phone.</p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-sm text-neutral-600">
                    <Check className="w-4 h-4 text-primary-600 shrink-0" /> One-tap rent payments
                  </li>
                  <li className="flex items-center gap-3 text-sm text-neutral-600">
                    <Check className="w-4 h-4 text-primary-600 shrink-0" /> Instant electricity token purchase
                  </li>
                  <li className="flex items-center gap-3 text-sm text-neutral-600">
                    <Check className="w-4 h-4 text-primary-600 shrink-0" /> Maintenance requests with photo upload
                  </li>
                  <li className="flex items-center gap-3 text-sm text-neutral-600">
                    <Check className="w-4 h-4 text-primary-600 shrink-0" /> Community announcements & events
                  </li>
                </ul>
                <button
                  onClick={() => showPage('/residents')}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                >
                  Learn more <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 4: EVERYTHING YOUR HOME NEEDS ========== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">Everything your home needs</h2>
            <p className="text-neutral-600 max-w-xl mx-auto">From paying rent to buying electricity tokens — Bilm handles it all in one place.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-6 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all group">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-large flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <CreditCard className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-lg font-bold text-neutral-900 mb-2">Rent Payments</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">Pay rent instantly with bank transfer, card, or USSD. Set up autopay and never miss a due date.</p>
            </div>
            <div className="p-6 rounded-large bg-teal-50 border border-teal-100 hover:shadow-custom transition-all group relative overflow-hidden">
              <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-large flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-lg font-bold text-neutral-900 mb-2">Buy Electricity</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">Purchase prepaid electricity tokens instantly. Get your token delivered in seconds, track usage history.</p>
            </div>
            <div className="p-6 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all group">
              <div className="w-12 h-12 bg-green-100 text-green-600 rounded-large flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Wrench className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-lg font-bold text-neutral-900 mb-2">Maintenance</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">Submit and track maintenance requests with photos. Get real-time updates on repair status.</p>
            </div>
            <div className="p-6 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all group">
              <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-large flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-lg font-bold text-neutral-900 mb-2">Community</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">Connect with neighbors, join events, and stay updated on building announcements.</p>
            </div>
            <div className="p-6 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all group">
              <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-large flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Package className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-lg font-bold text-neutral-900 mb-2">Package Management</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">Get notified when packages arrive. Track deliveries and pickup instructions.</p>
            </div>
            <div className="p-6 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all group">
              <div className="w-12 h-12 bg-rose-100 text-rose-600 rounded-large flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Bell className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-lg font-bold text-neutral-900 mb-2">Smart Notifications</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">Stay informed with smart alerts for rent due dates, maintenance updates, and building news.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold mb-4">
              <LayoutDashboard className="w-3 h-3" /> Admin Dashboard
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">Everything at your fingertips</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">A powerful dashboard that gives you complete visibility and control over your properties.</p>
          </div>

          {/* Dashboard mockup */}
          <div className="bg-neutral-900 rounded-2xl p-4 sm:p-6 shadow-2xl max-w-5xl mx-auto mb-16">
            {/* Top bar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="bg-neutral-800 rounded-lg px-4 py-1.5 text-neutral-400 text-xs">admin.bilm.app</div>
              <div></div>
            </div>

            {/* Dashboard content */}
            <div className="bg-background-50 rounded-xl p-4 sm:p-6">
              {/* Stats row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl p-4 border border-neutral-100">
                  <div className="text-neutral-500 text-xs mb-1">Properties</div>
                  <div className="font-heading text-2xl font-bold text-neutral-900">12</div>
                  <div className="text-xs text-green-600 mt-1">+2 this month</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-neutral-100">
                  <div className="text-neutral-500 text-xs mb-1">Total Units</div>
                  <div className="font-heading text-2xl font-bold text-neutral-900">248</div>
                  <div className="text-xs text-green-600 mt-1">96% occupied</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-neutral-100">
                  <div className="text-neutral-500 text-xs mb-1">Collection Rate</div>
                  <div className="font-heading text-2xl font-bold text-neutral-900">94.2%</div>
                  <div className="text-xs text-green-600 mt-1">+3.1% vs last month</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-neutral-100">
                  <div className="text-neutral-500 text-xs mb-1">Monthly Revenue</div>
                  <div className="font-heading text-2xl font-bold text-neutral-900">N8.4M</div>
                  <div className="text-xs text-green-600 mt-1">+12% growth</div>
                </div>
              </div>

              {/* Chart + Table row */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Mini bar chart */}
                <div className="bg-white rounded-xl p-4 border border-neutral-100">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-heading font-semibold text-sm text-neutral-900">Revenue (6 months)</span>
                    <span className="text-xs text-neutral-400">N millions</span>
                  </div>
                  <div className="flex items-end gap-2 h-32">
                    {[5.2, 6.1, 5.8, 7.3, 7.8, 8.4].map((val, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div
                          className={`w-full rounded-t-md ${i === 5 ? 'bg-primary-600' : 'bg-primary-200'}`}
                          style={{ height: `${(val / 8.4) * 100}%` }}
                        ></div>
                        <span className="text-[10px] text-neutral-400">{['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'][i]}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mini transactions table */}
                <div className="bg-white rounded-xl p-4 border border-neutral-100">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-heading font-semibold text-sm text-neutral-900">Recent Transactions</span>
                    <span className="text-xs text-primary-600">View all</span>
                  </div>
                  <div className="space-y-3">
                    {[
                      { name: 'Adebayo T.', type: 'Rent', amount: 'N150,000', status: 'Paid' },
                      { name: 'Chioma O.', type: 'Electricity', amount: 'N5,000', status: 'Paid' },
                      { name: 'Emeka N.', type: 'Rent', amount: 'N85,000', status: 'Pending' },
                      { name: 'Fatima B.', type: 'Service', amount: 'N25,000', status: 'Paid' },
                    ].map((tx, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-neutral-100 flex items-center justify-center text-xs font-semibold text-neutral-600">
                            {tx.name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-medium text-neutral-900 text-xs">{tx.name}</div>
                            <div className="text-neutral-400 text-[10px]">{tx.type}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-neutral-900 text-xs">{tx.amount}</div>
                          <div className={`text-[10px] ${tx.status === 'Paid' ? 'text-green-600' : 'text-amber-600'}`}>{tx.status}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature quick-link cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto">
            {[
              { icon: CreditCard, label: 'Rent Collection', color: 'bg-blue-50 text-blue-600' },
              { icon: Zap, label: 'Electricity Sales', color: 'bg-amber-50 text-amber-600' },
              { icon: Wrench, label: 'Maintenance', color: 'bg-green-50 text-green-600' },
              { icon: BarChart3, label: 'Reports', color: 'bg-purple-50 text-purple-600' },
              { icon: MessageSquare, label: 'Communication', color: 'bg-teal-50 text-teal-600' },
              { icon: FileText, label: 'Invoicing', color: 'bg-rose-50 text-rose-600' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-neutral-100 text-center hover:shadow-custom transition-all group">
                <div className={`w-10 h-10 ${item.color} rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-5 h-5" />
                </div>
                <div className="text-xs font-semibold text-neutral-700">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-neutral-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">Why property managers are switching to Bilm</h2>
            <p className="text-neutral-400 max-w-xl mx-auto">See how Bilm compares to traditional property management methods and other platforms.</p>
          </div>

          <div className="bg-neutral-800/50 rounded-2xl overflow-hidden border border-neutral-700">
            {/* Header row */}
            <div className="grid grid-cols-3 gap-4 p-4 sm:p-5 border-b border-neutral-700 bg-neutral-800/80">
              <div className="text-sm font-semibold text-neutral-400">Feature</div>
              <div className="text-sm font-semibold text-neutral-400 text-center">Traditional / Others</div>
              <div className="text-sm font-semibold text-primary-400 text-center">Bilm</div>
            </div>

            {/* Comparison rows */}
            {[
              { feature: 'Rent Collection', others: false, bilm: true, desc: 'Automated with reminders' },
              { feature: 'Electricity Vending', others: false, bilm: true, desc: 'Integrated DISCO tokens' },
              { feature: 'Maintenance Tracking', others: false, bilm: true, desc: 'Photo upload & status' },
              { feature: 'Financial Reports', others: false, bilm: true, desc: 'Real-time analytics' },
              { feature: 'Resident Communication', others: false, bilm: true, desc: 'In-app messaging' },
              { feature: 'Pricing', others: 'Expensive', bilm: true, desc: 'Affordable per-property plans' },
            ].map((row, i) => (
              <div key={i} className={`grid grid-cols-3 gap-4 p-4 sm:p-5 ${i < 5 ? 'border-b border-neutral-700/50' : ''}`}>
                <div>
                  <div className="text-sm font-medium text-white">{row.feature}</div>
                  <div className="text-xs text-neutral-500 mt-0.5 hidden sm:block">{row.desc}</div>
                </div>
                <div className="flex justify-center items-center">
                  {row.others === false ? (
                    <div className="w-7 h-7 rounded-full bg-red-500/10 flex items-center justify-center">
                      <X className="w-4 h-4 text-red-400" />
                    </div>
                  ) : (
                    <span className="text-xs text-neutral-400 font-medium">{row.others}</span>
                  )}
                </div>
                <div className="flex justify-center items-center">
                  <div className="w-7 h-7 rounded-full bg-green-500/10 flex items-center justify-center">
                    <Check className="w-4 h-4 text-green-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold mb-4">
              <Building2 className="w-3 h-3" /> For Property Managers
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">Simple, transparent pricing</h2>
            <p className="text-neutral-600 max-w-xl mx-auto">Per-property pricing for managers. Residents use the app for free. Scale as your portfolio grows.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            {/* Starter */}
            <div className="bg-background-50 rounded-2xl p-8 border border-neutral-200 relative">
              <div className="mb-6">
                <h3 className="font-heading text-lg font-bold text-neutral-900 mb-1">Starter</h3>
                <p className="text-neutral-500 text-sm">For small properties</p>
              </div>
              <div className="mb-2">
                <span className="font-heading text-4xl font-bold text-neutral-900">N25,000</span>
                <span className="text-neutral-500 text-sm">/property/mo</span>
              </div>
              <p className="text-xs text-neutral-400 mb-6">Up to 20 units per property</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm text-neutral-600"><Check className="w-4 h-4 text-green-500 shrink-0" /> Rent collection & reminders</li>
                <li className="flex items-center gap-2 text-sm text-neutral-600"><Check className="w-4 h-4 text-green-500 shrink-0" /> Electricity vending</li>
                <li className="flex items-center gap-2 text-sm text-neutral-600"><Check className="w-4 h-4 text-green-500 shrink-0" /> Maintenance tracking</li>
                <li className="flex items-center gap-2 text-sm text-neutral-600"><Check className="w-4 h-4 text-green-500 shrink-0" /> Basic reports</li>
                <li className="flex items-center gap-2 text-sm text-neutral-600"><Check className="w-4 h-4 text-green-500 shrink-0" /> Email support</li>
              </ul>
              <button
                onClick={() => window.location.href = 'https://admin.bilm.app/auth/sign-up'}
                className="w-full py-3 border border-neutral-300 text-neutral-700 font-semibold rounded-small text-sm hover:bg-neutral-50 transition-colors"
              >
                Get Started
              </button>
            </div>

            {/* Growth - Popular */}
            <div className="bg-primary-600 rounded-2xl p-8 text-white relative shadow-xl scale-[1.02]">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-amber-900 text-xs font-bold px-3 py-1 rounded-full">
                Most Popular
              </div>
              <div className="mb-6">
                <h3 className="font-heading text-lg font-bold mb-1">Growth</h3>
                <p className="text-blue-200 text-sm">For growing portfolios</p>
              </div>
              <div className="mb-2">
                <span className="font-heading text-4xl font-bold">N65,000</span>
                <span className="text-blue-200 text-sm">/property/mo</span>
              </div>
              <p className="text-xs text-blue-200 mb-6">Up to 100 units per property</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm text-blue-100"><Check className="w-4 h-4 text-green-300 shrink-0" /> Everything in Starter</li>
                <li className="flex items-center gap-2 text-sm text-blue-100"><Check className="w-4 h-4 text-green-300 shrink-0" /> Advanced analytics</li>
                <li className="flex items-center gap-2 text-sm text-blue-100"><Check className="w-4 h-4 text-green-300 shrink-0" /> Priority support</li>
                <li className="flex items-center gap-2 text-sm text-blue-100"><Check className="w-4 h-4 text-green-300 shrink-0" /> Custom branding</li>
                <li className="flex items-center gap-2 text-sm text-blue-100"><Check className="w-4 h-4 text-green-300 shrink-0" /> Resident communication</li>
              </ul>
              <button
                onClick={() => window.location.href = 'https://admin.bilm.app/auth/sign-up'}
                className="w-full py-3 bg-white text-primary-700 font-semibold rounded-small text-sm hover:bg-blue-50 transition-colors"
              >
                Get Started
              </button>
            </div>

            {/* Enterprise */}
            <div className="bg-background-50 rounded-2xl p-8 border border-neutral-200 relative">
              <div className="mb-6">
                <h3 className="font-heading text-lg font-bold text-neutral-900 mb-1">Enterprise</h3>
                <p className="text-neutral-500 text-sm">For large operations</p>
              </div>
              <div className="mb-2">
                <span className="font-heading text-4xl font-bold text-neutral-900">Custom</span>
              </div>
              <p className="text-xs text-neutral-400 mb-6">Unlimited units per property</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm text-neutral-600"><Check className="w-4 h-4 text-green-500 shrink-0" /> Everything in Growth</li>
                <li className="flex items-center gap-2 text-sm text-neutral-600"><Check className="w-4 h-4 text-green-500 shrink-0" /> Dedicated account manager</li>
                <li className="flex items-center gap-2 text-sm text-neutral-600"><Check className="w-4 h-4 text-green-500 shrink-0" /> API access</li>
                <li className="flex items-center gap-2 text-sm text-neutral-600"><Check className="w-4 h-4 text-green-500 shrink-0" /> SLA guarantee</li>
                <li className="flex items-center gap-2 text-sm text-neutral-600"><Check className="w-4 h-4 text-green-500 shrink-0" /> Free migration support</li>
              </ul>
              <a
                href="mailto:sales@bilm.app"
                className="block w-full py-3 border border-neutral-300 text-neutral-700 font-semibold rounded-small text-sm hover:bg-neutral-50 transition-colors text-center"
              >
                Contact Sales
              </a>
            </div>
          </div>

          {/* Reassurance row */}
          <div className="flex flex-wrap justify-center gap-8 text-sm text-neutral-500">
            <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-green-500" /> No hidden fees</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Flexible plans</span>
            <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" /> Electricity management included</span>
          </div>
        </div>
      </section>

      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1A2A6C 0%, #2D52B8 50%, #4F8EF7 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">Ready to transform your property management?</h2>
          <p className="text-blue-100 text-lg mb-10 max-w-xl mx-auto">Join 1,200+ properties already using Bilm to streamline their operations and delight residents.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <button
              onClick={() => window.location.href = 'https://admin.bilm.app/auth/sign-up'}
              className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold rounded-small text-primary-900 bg-white hover:bg-blue-50 shadow-lg transition-all gap-2"
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="mailto:sales@bilm.app"
              className="inline-flex items-center justify-center px-7 py-3.5 border border-white/30 text-base font-semibold rounded-small text-white hover:bg-white/10 transition-all gap-2 backdrop-blur-sm"
            >
              Book a Demo
            </a>
          </div>
          {/* App download links */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <span className="text-blue-200 text-sm">Download for residents:</span>
            <div className="flex gap-3">
              <a
                href="https://apps.apple.com/ng/app/bilm/id6471915924"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-small text-white text-sm font-medium transition-colors backdrop-blur-sm"
              >
                <Apple className="w-4 h-4" /> App Store
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.builm&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-small text-white text-sm font-medium transition-colors backdrop-blur-sm"
              >
                <Play className="w-4 h-4" /> Google Play
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer showPage={showPage} />
    </div>
  );
};

export default HomePage;
